import React, { useEffect, useState } from "react";
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Box,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  TablePagination,
  TableFooter,
  IconButton,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  paginationLabel,
  rowsPerPageLabel,
  MIN_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  TableContainerWrapper,
  TablePaginationStyle,
} from "../../Common/styles/table";
import { AxiosResponse } from "axios";
import { StatusBadge } from "../../Common/UI/StatusTag";
import AddInstrumentModal from "./AddInstrumentModal";
import { MoreVert } from "@mui/icons-material";

const statusMap: any = {
  active: { label: "Active", variant: "active" },
  inactive: { label: "Inactive", variant: "cancelled" },
};

type Props = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const InstrumentsList = ({ showModal, setShowModal }: Props) => {
  const [loading, setLoading] = useState(true);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [instruments, setInstruments] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [menuLoader, setMenuLoader] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSize);
  const [instrumentsCount, setInstrumentsCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchInstruments = async () => {
      try {
        setLoading(true);
        let url = `/instrument-library?page=${page + 1}&size=${rowsPerPage}`;

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.rows;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          title: item?.title || "",
          diseaseArea: item?.diseaseArea || "",
          source: item?.source || "",
          cost: item?.cost || "",
          fileUrl: item?.fileUrl || "",
          status: item?.status || "",
          description: item?.description || "",
        }));

        setInstruments(newData || []);
        setInstrumentsCount(res.data?.data?.count || 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchInstruments();
  }, [toggleLoader, page, rowsPerPage]);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openModal = () => {
    setShowModal(true);
    setAnchorEl(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const updateStatus = async (status: string) => {
    try {
      setMenuLoader(true);
      const res: AxiosResponse = await http.patch(
        `/instrument-library/${selectedRow?.id}/status`,
        {
          status,
        },
      );
      toastMessage("success", res.data.message);
      handleMenuClose();
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setMenuLoader(false);
    }
  };

  return (
    <TableContainerWrapper>
      <Table sx={TableBorderRadiusTopLeftRight}>
        <TableHead>
          <TableRow>
            <StyledTableCell>Title</StyledTableCell>
            <StyledTableCell>Disease Area</StyledTableCell>
            <StyledTableCell>Source / Location</StyledTableCell>
            <StyledTableCell>Approximate Cost</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell />
          </TableRow>
        </TableHead>
        {!loading && instruments?.length > 0 && (
          <>
            <TableBody>
              {instruments?.map((row: any) => (
                <TableRow key={row?.id}>
                  <StyledTableCell>{row?.title || "-"}</StyledTableCell>
                  <StyledTableCell>{row?.diseaseArea || "-"}</StyledTableCell>
                  <StyledTableCell>{row?.source || "-"}</StyledTableCell>
                  <StyledTableCell>{row?.cost || "-"}</StyledTableCell>
                  <StyledTableCell>
                    <StatusBadge
                      variant={
                        statusMap[
                          row?.status?.toLowerCase() === "active"
                            ? "active"
                            : "inactive"
                        ]?.variant || "active"
                      }
                      label={
                        statusMap[
                          row?.status?.toLowerCase() === "active"
                            ? "active"
                            : "inactive"
                        ]?.label
                      }
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <IconButton onClick={(e) => handleMenuClick(e, row)}>
                      <MoreVert />
                    </IconButton>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
            {instrumentsCount > MIN_ROWS_PER_PAGE && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={TablePaginationStyle}
                    count={instrumentsCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    labelDisplayedRows={paginationLabel}
                    labelRowsPerPage={rowsPerPageLabel}
                  />
                </TableRow>
              </TableFooter>
            )}
          </>
        )}
      </Table>
      {!loading && instruments.length === 0 && (
        <NoDataContainer>
          <Typography variant="body1" color="gray">
            No Data
          </Typography>
        </NoDataContainer>
      )}
      {loading && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: "400px",
            width: "20ch",
          },
        }}
        TransitionProps={{
          exit: false,
        }}
      >
        <MenuItem disabled={menuLoader} onClick={openModal}>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={menuLoader}
          onClick={() =>
            updateStatus(
              selectedRow?.status === "active" ? "inactive" : "active",
            )
          }
        >
          <ListItemText>
            {selectedRow?.status === "active" ? "De-activate" : "Activate"}
          </ListItemText>
          {menuLoader && (
            <ListItemIcon>
              <CircularProgress size={18} sx={{ ml: 1 }} />
            </ListItemIcon>
          )}
        </MenuItem>
      </Menu>

      {showModal && (
        <AddInstrumentModal
          showModal={showModal}
          closeModal={closeModal}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      )}
    </TableContainerWrapper>
  );
};

export default InstrumentsList;
